/**
 * Orquestração da chamada, do lado do console.
 *
 * Duas fontes alimentam o **mesmo** reducer:
 *
 *   - `sim` / `mic` — o roteiro versionado do backend, reproduzido localmente,
 *     para demonstrar sem consumir API;
 *   - o **espelho** de uma conversa real acontecendo em `/linha`.
 *
 * O console nunca abre microfone nem conexão WebRTC, e nunca origina a chamada:
 * a conversa nasce na linha e ele a segue automaticamente. É a mesma topologia
 * de produção, onde o áudio está numa ligação telefônica e o supervisor é
 * sempre um espelho.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import QRCode from 'qrcode'

import { api, ApiError } from './api'
import { callReducer, initialCallState, wordDelayMs, type OrbState } from './callEngine'
import { createAmplitudeAnalyser } from './realtime'
import { openStream, type StreamHandle, type StreamMessage } from './stream'
import { clock } from './format'
import type { Artifact, AudioSource, Effect, ScriptResponse, ToolExecutionResponse } from './types'

interface UseCallOptions {
  caseId: string
  scenarioId: string
  source: AudioSource
  /** Multiplicador de velocidade do roteiro simulado. */
  speed: number
}

export type LineStatus = 'offline' | 'waiting' | 'ringing' | 'connecting' | 'live' | 'ended'

export interface CallController {
  state: ReturnType<typeof callReducer>
  callId: string | null
  script: ScriptResponse | null
  connecting: boolean
  error: string | null
  /** Quantas telas de chamada estão conectadas ao canal de espelhamento. */
  linePeers: number
  lineStatus: LineStatus
  /** True quando o console está espelhando uma conversa real. */
  isMirroring: boolean
  amplitudeRef: React.MutableRefObject<(() => number) | null>
  start(): Promise<void>
  hangup(): Promise<void>
  reset(): void
  toggleHuman(): void
  toggleMute(): void
  whisper(text: string): void
  sendClientText(text: string): void
}

export function useCall(options: UseCallOptions): CallController {
  const [state, dispatch] = useReducer(callReducer, initialCallState)
  const [callId, setCallId] = useState<string | null>(null)
  const [script, setScript] = useState<ScriptResponse | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [linePeers, setLinePeers] = useState(0)
  const [lineStatus, setLineStatus] = useState<LineStatus>('offline')
  const [isMirroring, setIsMirroring] = useState(false)

  const timers = useRef<number[]>([])
  const aborted = useRef(false)
  const amplitudeRef = useRef<(() => number) | null>(null)
  const analyserDispose = useRef<(() => void) | null>(null)
  const humanPaused = useRef(false)
  const elapsedRef = useRef(0)
  const callIdRef = useRef<string | null>(null)
  const stream = useRef<StreamHandle | null>(null)
  const mirroring = useRef(false)
  const scriptRef = useRef<ScriptResponse | null>(null)

  elapsedRef.current = state.elapsed
  humanPaused.current = state.humanInControl

  const at = useCallback(() => clock(elapsedRef.current), [])

  const clearTimers = useCallback(() => {
    for (const id of timers.current) window.clearTimeout(id)
    timers.current = []
  }, [])

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms)
    timers.current.push(id)
    return id
  }, [])

  // ------------------------------------------------------------- artefatos --
  const scheduleArtifactTimeline = useCallback(
    (artifact: Artifact, timeline: ToolExecutionResponse['timeline']) => {
      for (const stage of timeline) {
        later(() => {
          dispatch({
            type: 'patchArtifact',
            id: artifact.id,
            patch: {
              status: stage.status,
              status_label: stage.label,
              ...(stage.progress_pct !== undefined ? { progress_pct: stage.progress_pct } : {}),
              ...(stage.progress_label !== undefined
                ? { progress_label: stage.progress_label }
                : {}),
            },
          })
        }, stage.delay_ms)
      }
    },
    [later],
  )

  // ------------------------------------------------------- canal de espelho --
  const onStreamMessage = useCallback(
    (message: StreamMessage) => {
      switch (message.type) {
        case 'peers':
        case 'hello':
          setLinePeers(message.peers.line ?? 0)
          setLineStatus((current) =>
            (message.peers.line ?? 0) === 0
              ? 'offline'
              : current === 'offline'
                ? 'waiting'
                : current,
          )
          break

        case 'dial': {
          // A chamada nasceu na linha. O console entra em modo espelho sozinho,
          // independentemente da fonte selecionada aqui — se há conversa
          // acontecendo, o supervisor tem que ver.
          mirroring.current = true
          setIsMirroring(true)
          setCallId(message.call_id)
          callIdRef.current = message.call_id
          setLineStatus('connecting')
          setError(null)
          void api
            .createCallMirror(message.call_id)
            .then((call) => {
              dispatch({
                type: 'start',
                guardrails: scriptRef.current?.initial_guardrails ?? [],
                capPct: call.cap_pct,
              })
              for (const entry of call.audit) dispatch({ type: 'addAudit', entry })
            })
            .catch(() => {
              dispatch({
                type: 'start',
                guardrails: scriptRef.current?.initial_guardrails ?? [],
                capPct: 0,
              })
            })
          break
        }

        case 'answered':
          setLineStatus('connecting')
          break

        case 'declined':
          setLineStatus('waiting')
          setError('A cliente recusou a chamada no aparelho.')
          break

        case 'connecting':
          setLineStatus('connecting')
          break

        case 'connected':
          setLineStatus('live')
          setConnecting(false)
          void auditRef.current?.(
            `Conexão WebRTC estabelecida · modelo ${message.model} · voz ${message.voice}`,
          )
          break

        case 'failed':
          setLineStatus('ended')
          setConnecting(false)
          setError(`Falha no aparelho: ${message.reason}`)
          break

        case 'tick':
          dispatch({ type: 'syncElapsed', elapsed: message.elapsed })
          break

        case 'orb':
          dispatch({ type: 'orb', state: message.state as OrbState })
          break

        case 'transcript':
          dispatch({
            type: 'appendText',
            speaker: message.speaker,
            text: message.delta,
            at: message.at,
          })
          break

        case 'turn_done':
          dispatch({ type: 'orb', state: 'think' })
          break

        case 'effect':
          if (message.effect.type === 'audit') {
            void auditRef.current?.(String(message.effect.payload.event))
          } else {
            dispatch({ type: 'applyEffect', effect: message.effect, at: message.at })
          }
          break

        case 'artifact':
          dispatch({ type: 'addArtifact', artifact: message.artifact })
          scheduleArtifactTimeline(message.artifact, message.timeline)
          break

        case 'ended':
          setLineStatus('ended')
          dispatch({ type: 'end' })
          break

        default:
          break
      }
    },
    [scheduleArtifactTimeline],
  )

  useEffect(() => {
    stream.current = openStream('supervisor', {
      onMessage: onStreamMessage,
      onStatusChange: (connectedNow) => {
        if (!connectedNow) setLineStatus('offline')
      },
    })
    return () => {
      stream.current?.close()
      stream.current = null
    }
  }, [onStreamMessage])

  // Carrega o roteiro do cenário selecionado.
  useEffect(() => {
    let cancelled = false
    api
      .script(options.scenarioId)
      .then((data) => {
        if (cancelled) return
        setScript(data)
        scriptRef.current = data
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Falha ao carregar o roteiro')
      })
    return () => {
      cancelled = true
    }
  }, [options.scenarioId])

  // Relógio local — no modo espelho quem manda é o `tick` do aparelho.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!mirroring.current) dispatch({ type: 'tick' })
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(
    () => () => {
      aborted.current = true
      clearTimers()
      analyserDispose.current?.()
    },
    [clearTimers],
  )

  // ---------------------------------------------------------------- auditoria --
  const audit = useCallback(
    async (event: string) => {
      const id = callIdRef.current
      if (!id) return
      try {
        const entry = await api.appendAudit(id, event, at())
        dispatch({ type: 'addAudit', entry })
      } catch {
        // A trilha do servidor é a fonte da verdade; uma falha de rede não pode
        // derrubar a chamada em andamento.
      }
    },
    [at],
  )

  const auditRef = useRef<((event: string) => Promise<void>) | null>(null)
  auditRef.current = audit

  // ---------------------------------------------------- ferramentas (roteiro) --
  const runTool = useCallback(
    async (name: string, args: Record<string, unknown>): Promise<ToolExecutionResponse | null> => {
      const id = callIdRef.current
      if (!id) return null
      try {
        const response = await api.executeTool(id, name, args, at())
        const artifact = { ...response.artifact }

        if (artifact.kind === 'pix' && artifact.code) {
          try {
            artifact.qr_svg = await QRCode.toString(artifact.code, {
              type: 'svg',
              margin: 0,
              errorCorrectionLevel: 'M',
            })
          } catch {
            artifact.qr_svg = null
          }
        }

        dispatch({ type: 'addArtifact', artifact })
        dispatch({ type: 'addAudit', entry: response.audit })
        scheduleArtifactTimeline(artifact, response.timeline)
        return response
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          void audit(`Ferramenta bloqueada pela política: ${name}`)
        }
        return null
      }
    },
    [at, audit, scheduleArtifactTimeline],
  )

  const applyEffects = useCallback(
    async (effects: Effect[]) => {
      for (const effect of effects) {
        if (effect.type === 'audit') {
          await audit(String(effect.payload.event))
        } else if (effect.type === 'add_artifact') {
          await runTool(
            String(effect.payload.tool),
            (effect.payload.arguments ?? {}) as Record<string, unknown>,
          )
        } else {
          dispatch({ type: 'applyEffect', effect, at: at() })
        }
      }
    },
    [audit, runTool, at],
  )

  // ------------------------------------------------------------ roteiro simulado --
  const hangupRef = useRef<(() => Promise<void>) | null>(null)

  const runScript = useCallback(
    (steps: ScriptResponse['steps'], index: number) => {
      if (aborted.current) return

      const step = steps[index]
      if (!step) {
        later(() => void hangupRef.current?.(), 1600)
        return
      }

      // Enquanto o operador humano está no comando, o roteiro espera.
      if (humanPaused.current) {
        later(() => runScript(steps, index), 600)
        return
      }

      const words = step.text.split(' ')
      dispatch({ type: 'beginTurn', speaker: step.speaker, at: at() })

      let cursor = 0
      const streamWord = () => {
        if (aborted.current) return
        cursor += 1
        dispatch({ type: 'setTurnText', text: words.slice(0, cursor).join(' ') })

        if (cursor < words.length) {
          later(streamWord, wordDelayMs(words[cursor - 1], options.speed))
          return
        }

        if (step.marks.length > 0) dispatch({ type: 'setTurnMarks', marks: step.marks })

        void applyEffects(step.effects).then(() => {
          if (aborted.current) return
          dispatch({ type: 'orb', state: 'think' })
          later(() => runScript(steps, index + 1), 780 / options.speed)
        })
      }

      later(streamWord, 180)
    },
    [later, at, applyEffects, options.speed],
  )

  // ------------------------------------------------------------------ ações --
  const start = useCallback(async () => {
    if (!script) return
    setError(null)
    setConnecting(true)
    aborted.current = false
    clearTimers()

    try {
      const call = await api.createCall(options.caseId, options.scenarioId, options.source)
      setCallId(call.id)
      callIdRef.current = call.id
      dispatch({ type: 'start', guardrails: script.initial_guardrails, capPct: call.cap_pct })
      dispatch({ type: 'addAudit', entry: call.audit[0] })

      mirroring.current = false
      setIsMirroring(false)

      if (options.source === 'mic') {
        try {
          const micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
          const analyser = createAmplitudeAnalyser(micStream)
          amplitudeRef.current = analyser.read
          analyserDispose.current = () => {
            analyser.dispose()
            for (const track of micStream.getTracks()) track.stop()
          }
        } catch {
          // Permissão negada: o orb volta para a amplitude sintética.
          amplitudeRef.current = null
        }
      }

      later(() => runScript(script.steps, 0), 700)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao iniciar a chamada')
    } finally {
      setConnecting(false)
    }
  }, [
    script,
    options.caseId,
    options.scenarioId,
    options.source,
    clearTimers,
    later,
    runScript,
    audit,
  ])

  const hangup = useCallback(async () => {
    clearTimers()
    if (mirroring.current) stream.current?.send({ type: 'command', command: 'hangup' })
    analyserDispose.current?.()
    analyserDispose.current = null
    amplitudeRef.current = null
    mirroring.current = false
    dispatch({ type: 'end' })
  }, [clearTimers])

  hangupRef.current = hangup

  const reset = useCallback(() => {
    aborted.current = false
    clearTimers()
    mirroring.current = false
    setIsMirroring(false)
    setCallId(null)
    callIdRef.current = null
    setError(null)
    setLineStatus((current) => (current === 'offline' ? 'offline' : 'waiting'))
    dispatch({
      type: 'reset',
      guardrails: script?.initial_guardrails ?? [],
      capPct: state.capPct,
    })
  }, [clearTimers, script, state.capPct])

  const toggleHuman = useCallback(() => {
    const next = !humanPaused.current
    dispatch({ type: 'toggleHuman' })
    stream.current?.send({ type: 'command', command: 'takeover', human: next })
    void audit(next ? 'Operador humano assumiu a chamada' : 'Controle devolvido à IA')
  }, [audit])

  const toggleMute = useCallback(() => {
    const next = !state.muted
    dispatch({ type: 'toggleMute' })
    stream.current?.send({ type: 'command', command: 'mute', muted: next })
    void audit(next ? 'Linha em mudo' : 'Linha reativada')
  }, [state.muted, audit])

  const whisper = useCallback(
    (text: string) => {
      stream.current?.send({ type: 'command', command: 'whisper', text })
      void audit(`Sussurro do supervisor: "${text}"`)
      dispatch({ type: 'beginTurn', speaker: 'ai', at: at() })
      dispatch({
        type: 'setTurnMarks',
        marks: [{ kind: 'ctx', label: `Sussurro do supervisor recebido: ${text}` }],
      })
    },
    [audit, at],
  )

  const sendClientText = useCallback(
    (text: string) => {
      dispatch({ type: 'appendText', speaker: 'cli', text, at: at() })
      void audit(`Entrada por texto (fallback sem áudio): "${text.slice(0, 48)}"`)
    },
    [audit, at],
  )

  return useMemo(
    () => ({
      state,
      callId,
      script,
      connecting,
      error,
      linePeers,
      lineStatus,
      isMirroring,
      amplitudeRef,
      start,
      hangup,
      reset,
      toggleHuman,
      toggleMute,
      whisper,
      sendClientText,
    }),
    [
      state,
      callId,
      script,
      connecting,
      error,
      linePeers,
      lineStatus,
      isMirroring,
      start,
      hangup,
      reset,
      toggleHuman,
      toggleMute,
      whisper,
      sendClientText,
    ],
  )
}
